/**
 * 多账号动作（14.34b；数据链 14.34a）：账号管理页只调这里，不直接碰 RPC。
 *
 * 切换成功的主线程收尾（Worker 已完成整条切换链：凭证落位+旧库清理+新 session 已启）：
 * 1. **清栈回首页**——popWhile 直跳栈底：旧账号的会话页/设置页不能留在历史里
 *    （返回键会落进已被清库的页面）。popstate 异步完成，等 nav 落定再继续
 *    （兜底计时放行，popstate 丢失时降级为只换 auth 不清栈，不卡死切换）。
 * 2. **auth 两相过渡** logged-out→logged-in（两次 flushSync）——复用全部切片
 *    既有的登出重置/登录重拉路径（conversations/drafts 按 uid 重拉、config/
 *    stickers/contacts 走 logged-out 清空），不需要每个切片单独补「logged-in
 *    之间 uid 变化」分支。两相之间没有绘制（Preact 渲染异步），登录页不会闪现；
 *    期间守卫的 to-login/leave-login 只是同步改栈，最后 replace 回首页找平。
 * 3. 目标账号 needsPassword 时不做第 3 步的 replace——守卫已强制 to-set-password，
 *    覆盖它会把人踢出设密码页。
 */
import type { AppWorkerHost } from './authState';
import type { AuthUser } from '~/worker/contract';
import type { GlobalStore } from './global';
import { loginErrorMessage } from './authActions';
import { HOME_CHATS } from '~/nav/routes';
import type { Router } from '~/nav/router';
import type { AccountSummary } from '~/worker/contract';

export type AccountsListOutcome =
  | { readonly ok: true; readonly accounts: AccountSummary[] }
  | { readonly ok: false; readonly message: string };

/** 账号列表（纯 IndexedDB 零 HTTP；当前账号已排最前，requests 场景 AF） */
export async function loadStoredAccounts(host: AppWorkerHost): Promise<AccountsListOutcome> {
  try {
    const { accounts } = await host.call('auth/accounts', null);
    return { ok: true, accounts };
  } catch (err) {
    return { ok: false, message: loginErrorMessage(err, '读取账号列表失败，请重试') };
  }
}

export type SwitchOutcome =
  /** 切换完成，导航已回首页（或设密码页），账号管理页随之卸载 */
  | { readonly kind: 'ok' }
  /** 目标 refreshToken 已失效，**记录已被 Worker 删掉**——提示重新登录并刷新列表 */
  | { readonly kind: 'expired' }
  /** 网络错误/记录不存在等：当前账号未动，可原地重试 */
  | { readonly kind: 'error'; readonly message: string };

/** 等页面栈收拢到栈底（popWhile 的 popstate 异步完成）；超时放行防卡死 */
function collapseNavToRoot(router: Router, store: GlobalStore): Promise<void> {
  if (router.getNav().stack.length <= 1) return Promise.resolve();
  router.popWhile(() => true);
  // 测试用 FakeRouter 可能同步完成
  if (router.getNav().stack.length <= 1) return Promise.resolve();
  return new Promise((resolve) => {
    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      off();
      clearTimeout(timer);
      resolve();
    };
    const off = store.subscribe(
      (s) => s.nav,
      () => {
        if (router.getNav().stack.length <= 1) finish();
      },
    );
    const timer = setTimeout(finish, 1500);
  });
}

/**
 * 账号已在 Worker 侧换好后的主线程收尾（文件头三步；切换与添加账号登录共用——
 * 添加账号经 LoginCommit 注入 authActions.performLogin，14.34b-2）。
 */
export async function settleAccountSwitch(
  store: GlobalStore,
  router: Router,
  user: AuthUser,
): Promise<void> {
  await collapseNavToRoot(router, store);
  store.set({ auth: { status: 'logged-out' } });
  store.flushSync();
  store.set({ auth: { status: 'logged-in', ...user } });
  store.flushSync();
  if (!user.needsPassword) {
    router.replace(HOME_CHATS);
    // 立即冲刷 nav 通知：让「回首页」的卸载渲染先于下一帧的 effect 冲刷——
    // 否则栈收拢瞬间顶上来的我的 Tab 会抢在卸载前跑 effect 拉 profile（场景 AF 超预算）
    store.flushSync();
  }
}

export async function switchStoredAccount(
  host: AppWorkerHost,
  store: GlobalStore,
  router: Router,
  userId: number,
): Promise<SwitchOutcome> {
  let result;
  try {
    result = await host.call('auth/switch', { userId });
  } catch (err) {
    return { kind: 'error', message: loginErrorMessage(err, '切换失败，请稍后重试') };
  }
  if (!result.ok) {
    if (result.reason === 'refresh-failed') return { kind: 'expired' };
    return { kind: 'error', message: '账号记录不存在，请刷新后重试' };
  }
  await settleAccountSwitch(store, router, result.user);
  return { kind: 'ok' };
}

/** 移除非当前账号的凭证记录（纯本地零 HTTP；账号库数据保留，重登秒开）。
 *  false=当前账号被拒/Worker 异常，UI 提示后刷新列表找平 */
export async function removeStoredAccount(host: AppWorkerHost, userId: number): Promise<boolean> {
  try {
    return await host.call('auth/accounts/remove', { userId });
  } catch {
    return false;
  }
}
